import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { trackFormSubmit, openCalendlyDemo } from "@/lib/analytics";

const contactFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  company: z.string().trim().max(100, "Company name must be less than 100 characters").optional(),
  phone: z.string().trim().max(20, "Phone must be less than 20 characters").optional(),
  erpSystem: z.string().optional(),
  invoiceVolume: z.string().optional(),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters"),
  honeypot: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([{
          name: data.name,
          email: data.email,
          company: data.company || null,
          phone: data.phone || null,
          erp_system: data.erpSystem || null,
          invoice_volume: data.invoiceVolume || null,
          message: data.message,
        }]);

      if (error) throw error;

      // Send email notification via SendGrid
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone,
          erpSystem: data.erpSystem,
          invoiceVolume: data.invoiceVolume,
          message: data.message,
          honeypot: data.honeypot,
        },
      });

      if (emailError) {
        console.error('Error sending email:', emailError);
        // Check if it's a spam/profanity error
        if (emailError.message?.includes('inappropriate content')) {
          throw new Error('Your message contains inappropriate content. Please revise and resubmit.');
        }
        // Don't throw - form was saved successfully
      }

      trackFormSubmit('contact', {
        erp_system: data.erpSystem || undefined,
        invoice_volume: data.invoiceVolume || undefined,
        has_company: !!data.company,
        has_phone: !!data.phone,
      });

      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you within 24 hours.",
      });
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        variant: "destructive",
        title: "Error sending message",
        description: "Please try again or contact us directly at hello@ezcloud.ai",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-section-gradient">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 id="contact" className="text-3xl lg:text-5xl font-bold text-foreground mb-6 scroll-mt-40">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Contact us today to learn how EZ Cloud can transform your accounts payable process.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="h-full">
            <Card className="border-none shadow-medium bg-white/80 backdrop-blur-sm h-full flex flex-col">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-foreground mb-6">
                  Get In Touch
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8 flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  We're here to help transform your accounts payable process. Reach out to our team to discuss your automation needs, schedule a personalized demo, or get answers to your questions.
                </p>
                
                <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Phone className="w-6 h-6 text-accent" />
                  <div>
                    <div className="font-medium text-foreground">Phone</div>
                    <div className="text-muted-foreground">+1 856-834-5080</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <MapPin className="w-6 h-6 text-accent" />
                  <div>
                    <div className="font-medium text-foreground">Address</div>
                    <div className="text-muted-foreground">1134 S Black Horse Pike Suite 266<br />Blackwood, NJ 08012</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Clock className="w-6 h-6 text-accent" />
                  <div>
                    <div className="font-medium text-foreground">Business Hours</div>
                    <div className="text-muted-foreground">Monday - Friday: 9AM - 6PM EST</div>
                    <div className="text-muted-foreground text-sm mt-1">Response within 24 hours</div>
                  </div>
                </div>
                </div>
                
                <div className="pt-8 border-t border-border space-y-4">
                  <div>
                    <p className="font-medium text-foreground mb-2">Need immediate assistance?</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      For urgent inquiries or technical support, please call us directly during business hours. We're committed to providing prompt, personalized service to help you get the most out of EZ Cloud.
                    </p>
                  </div>
                  <div className="mt-4">
                    <p className="font-medium text-foreground mb-2">What to expect:</p>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-start">
                        <span className="text-accent mr-2">•</span>
                        <span>Response to all inquiries within 24 hours</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-accent mr-2">•</span>
                        <span>Free consultation and system assessment</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-accent mr-2">•</span>
                        <span>Personalized demo tailored to your needs</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="h-full">
            <Card className="border-none shadow-medium bg-white/80 backdrop-blur-sm h-full flex flex-col">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-foreground mb-6">
                  Send Us a Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      {...register("name")}
                      className="mt-1"
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.com"
                      {...register("email")}
                      className="mt-1"
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        placeholder="Acme Inc."
                        {...register("company")}
                        className="mt-1"
                      />
                      {errors.company && (
                        <p className="text-sm text-destructive mt-1">{errors.company.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        {...register("phone")}
                        className="mt-1"
                      />
                      {errors.phone && (
                        <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="erpSystem">ERP System</Label>
                      <Controller
                        name="erpSystem"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger className="mt-1 bg-popover" aria-label="Select your ERP system">
                              <SelectValue placeholder="Select your ERP" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover z-50">
                              <SelectItem value="Oracle EBS">Oracle EBS</SelectItem>
                              <SelectItem value="Oracle Cloud ERP">Oracle Cloud ERP</SelectItem>
                              <SelectItem value="SAP">SAP</SelectItem>
                              <SelectItem value="Workday">Workday</SelectItem>
                              <SelectItem value="Microsoft Dynamics">Microsoft Dynamics</SelectItem>
                              <SelectItem value="NetSuite">NetSuite</SelectItem>
                              <SelectItem value="Yardi Voyager">Yardi Voyager</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.erpSystem && (
                        <p className="text-sm text-destructive mt-1">{errors.erpSystem.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="invoiceVolume">Monthly Invoice Volume</Label>
                      <Controller
                        name="invoiceVolume"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger className="mt-1 bg-popover" aria-label="Select monthly invoice volume">
                              <SelectValue placeholder="Select volume" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover z-50">
                              <SelectItem value="<1,000">&lt;1,000</SelectItem>
                              <SelectItem value="1,000-5,000">1,000-5,000</SelectItem>
                              <SelectItem value="5,000-10,000">5,000-10,000</SelectItem>
                              <SelectItem value="10,000-25,000">10,000-25,000</SelectItem>
                              <SelectItem value="25,000-50,000">25,000-50,000</SelectItem>
                              <SelectItem value="50,000+">50,000+</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.invoiceVolume && (
                        <p className="text-sm text-destructive mt-1">{errors.invoiceVolume.message}</p>
                      )}
                    </div>
                  </div>

                   <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your AP automation needs..."
                      rows={5}
                      {...register("message")}
                      className="mt-1"
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Honeypot field - hidden from users but visible to bots */}
                  <input
                    type="text"
                    {...register("honeypot")}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  <Button
                    type="submit" 
                    variant="accent" 
                    size="lg" 
                    className="w-full text-lg py-6"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>

                  <div className="pt-4 border-t border-border text-center">
                    <p className="text-sm text-muted-foreground mb-3">
                      Or schedule a demo directly:
                    </p>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="lg" 
                      className="w-full"
                      onClick={() => openCalendlyDemo()}
                    >
                      Schedule on Calendly
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;